/**
 * Mobile device detection utilities
 */

/**
 * Check if the current browser is on a mobile device
 */
export function isMobileBrowser(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check for mobile user agent strings
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'android',
    'webos',
    'iphone',
    'ipad',
    'ipod',
    'blackberry',
    'windows phone',
    'opera mini',
    'iemobile',
    'mobile',
  ];

  const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));

  // Also check for touch capability and small screen as fallback
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  return isMobileUA || (hasTouchScreen && isSmallScreen);
}

/**
 * Check if the device is running iOS
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  return (
    userAgent.includes('iphone') ||
    userAgent.includes('ipad') ||
    userAgent.includes('ipod') ||
    // iPad on iOS 13+ reports as Macintosh
    (userAgent.includes('macintosh') && 'ontouchstart' in window)
  );
}

/**
 * Check if the device is running Android
 */
export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return navigator.userAgent.toLowerCase().includes('android');
}

/**
 * Get the appropriate app store URL for the current platform
 */
export function getAppStoreUrl(): string {
  if (isIOS()) {
    // Apple App Store URL (placeholder - replace with actual App Store ID)
    return 'https://apps.apple.com/app/rumio/id000000000';
  }

  if (isAndroid()) {
    // Google Play Store URL (placeholder - replace with actual package name)
    return 'https://play.google.com/store/apps/details?id=com.rumio.app';
  }

  // Fallback to website
  return 'https://rumio.world';
}

/**
 * Build a deep link URL for auth sync
 */
export function buildAuthDeepLink(accessToken: string, refreshToken: string | null): string {
  const params = new URLSearchParams({
    access_token: accessToken,
  });

  if (refreshToken) {
    params.set('refresh_token', refreshToken);
  }

  return `rumio://auth/sync?${params.toString()}`;
}

/**
 * Attempt to open the app with auth sync deep link.
 * Falls back to app store if app is not installed.
 */
export function openAppWithAuth(accessToken: string, refreshToken: string | null): void {
  const deepLink = buildAuthDeepLink(accessToken, refreshToken);
  const appStoreUrl = getAppStoreUrl();

  // Try to open deep link
  const startTime = Date.now();

  // Create a hidden iframe to attempt opening the app
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = deepLink;
  document.body.appendChild(iframe);

  // Also try window.location for better compatibility
  setTimeout(() => {
    window.location.href = deepLink;
  }, 25);

  // If still on this page after delay, app is likely not installed
  setTimeout(() => {
    // If we're still here and not much time has passed, app didn't open
    if (Date.now() - startTime < 2000) {
      // Redirect to app store
      window.location.href = appStoreUrl;
    }
    // Clean up iframe
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, 1500);
}
